import { CheckIcon, PencilIcon, XIcon } from "lucide-react";
import { useState, type KeyboardEvent } from "react";

const PROJECT_WORK_ITEM_PREFIX_PATTERN = /^[A-Z][A-Z0-9]{1,9}$/;

export interface ProjectWorkItemPrefixValueProps {
  readonly value: string | null | undefined;
  readonly onSave: (workItemPrefix: string | null) => Promise<void>;
}

export function ProjectWorkItemPrefixValue(props: ProjectWorkItemPrefixValueProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(props.value ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const normalizedDraft = draft.trim().toUpperCase();

  const beginEditing = () => {
    setDraft(props.value ?? "");
    setError(null);
    setEditing(true);
  };

  const cancelEditing = () => {
    setDraft(props.value ?? "");
    setError(null);
    setEditing(false);
  };

  const save = async () => {
    if (normalizedDraft.length > 0 && !PROJECT_WORK_ITEM_PREFIX_PATTERN.test(normalizedDraft)) {
      setError("Use 2–10 uppercase letters or numbers, starting with a letter.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await props.onSave(normalizedDraft.length > 0 ? normalizedDraft : null);
      setDraft(normalizedDraft);
      setEditing(false);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not save the task key.");
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      cancelEditing();
    } else if (event.key === "Enter") {
      event.preventDefault();
      void save();
    }
  };

  if (!editing) {
    return (
      <button
        type="button"
        aria-label="Edit project task key"
        className="inline-flex items-center gap-1 rounded border border-border bg-muted px-1.5 py-0.5 text-xs font-semibold tracking-wide text-foreground hover:bg-muted"
        onClick={beginEditing}
      >
        <span>{props.value ?? "Set key"}</span>
        <PencilIcon className="size-3 text-muted-foreground" aria-hidden />
      </button>
    );
  }

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1">
        <input
          aria-label="Project task key"
          autoFocus
          className="h-7 w-24 rounded border border-input bg-background px-2 text-xs font-semibold uppercase tracking-wide text-foreground outline-none focus:border-ring"
          disabled={saving}
          maxLength={10}
          placeholder="FOR"
          value={draft}
          onChange={(event) => {
            setDraft(event.target.value.toUpperCase());
            setError(null);
          }}
          onKeyDown={handleKeyDown}
        />
        <button
          type="button"
          aria-label="Save project task key"
          className="inline-flex size-7 items-center justify-center rounded text-primary hover:bg-muted disabled:opacity-50"
          disabled={saving}
          onClick={() => void save()}
        >
          <CheckIcon className="size-3.5" aria-hidden />
        </button>
        <button
          type="button"
          aria-label="Cancel project task key edit"
          className="inline-flex size-7 items-center justify-center rounded text-muted-foreground hover:bg-muted disabled:opacity-50"
          disabled={saving}
          onClick={cancelEditing}
        >
          <XIcon className="size-3.5" aria-hidden />
        </button>
      </div>
      {error !== null ? (
        <p role="alert" className="max-w-48 text-[11px] leading-tight text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}
